import { z } from 'zod';
import {
  ReversibleStockMovementV1Schema,
  StockCommandV1Schema,
  type StockCommandV1,
} from '../domain/stockCommand.js';

export {
  deepEqualJson,
  isNoOpMasterPatch,
  SERVER_MANAGED_MASTER_FIELDS,
} from './conflict.js';

export const SharedTabletOperatorAssertionSchema = z.object({
  grant_id: z.string().uuid(),
  grant_token: z.string().regex(/^[0-9a-f]{64}$/),
  tenant_personnel_id: z.string().uuid(),
});
export type SharedTabletOperatorAssertion = z.infer<
  typeof SharedTabletOperatorAssertionSchema
>;

export const SyncEventInputSchema = z.object({
  client_uuid: z.string().uuid(),
  kind: z.string(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().nullable(),
  device_id: z.string(),
  client_ts: z.string().datetime(),
  payload: z.record(z.unknown()),
  operator_assertion: SharedTabletOperatorAssertionSchema.optional(),
});
export type SyncEventInput = z.infer<typeof SyncEventInputSchema>;

export const MasterOpSchema = z.object({
  op: z.enum(['insert', 'update', 'archive', 'upsert']),
  table: z.string(),
  id: z.string().uuid(),
  patch: z.record(z.unknown()),
  base_version: z.number().int().nonnegative(),
  device_id: z.string(),
  client_ts: z.string().datetime(),
  operator_assertion: SharedTabletOperatorAssertionSchema.optional(),
});
export type MasterOp = z.infer<typeof MasterOpSchema>;

/**
 * Narrow master mutations that may share the Stock Command transaction.
 *
 * V1 intentionally limits the surface to the two masters that production
 * writers must create/archive together with stock. The server repeats a
 * column whitelist, tenant/FK checks and optimistic version checks.
 */
export const StockCommandMasterEffectV1Schema = z.object({
  op: z.enum(['insert', 'update', 'archive']),
  table: z.enum([
    'foods',
    'productions',
    'production_label_print_intents_v1',
  ]),
  id: z.string().uuid(),
  patch: z.record(z.unknown()),
  base_version: z.number().int().nonnegative(),
});
export type StockCommandMasterEffectV1 = z.infer<
  typeof StockCommandMasterEffectV1Schema
>;

export const StockCommandEnvelopeV1Schema = z.object({
  schema: z.literal('trackeat.stock-command'),
  version: z.literal(1),
  command: StockCommandV1Schema,
  command_digest: z.string().regex(/^[0-9a-f]{64}$/),
  device_id: z.string().trim().min(1).max(200),
  operator_assertion: SharedTabletOperatorAssertionSchema.optional(),
  events: z.array(SyncEventInputSchema).max(2_000),
  // Production writers and their reverse use this narrow compound path so a
  // required master cannot commit without its stock facts (or vice versa).
  master_effects: z
    .array(StockCommandMasterEffectV1Schema)
    .max(1_000)
    .default([]),
}).superRefine((envelope, ctx) => {
  const eventAssertions = envelope.events
    .map((event) => event.operator_assertion)
    .filter((assertion) => assertion !== undefined);
  const assertions = [
    ...(envelope.operator_assertion ? [envelope.operator_assertion] : []),
    ...eventAssertions,
  ];
  if (assertions.length > 0) {
    const expected = assertions[0];
    if (
      (envelope.events.length > 0 && eventAssertions.length !== envelope.events.length)
      || assertions.some((assertion) => (
        assertion.grant_id !== expected?.grant_id
        || assertion.grant_token !== expected?.grant_token
        || assertion.tenant_personnel_id
          !== expected?.tenant_personnel_id
      ))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operator_assertion'],
        message: 'L\'asserzione operatore deve essere unica e coerente per tutto il comando',
      });
    }
  }
  if (envelope.events.length === 0 && envelope.master_effects.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['events'],
      message: 'Il compound deve contenere eventi stock o effetti master',
    });
  }
  if (
    envelope.events.length === 0
    && envelope.command.movement_class !== 'production'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['command', 'movement_class'],
      message: 'Solo production supporta un compound master-only',
    });
  }
  if (
    envelope.master_effects.length > 0
    && envelope.command.movement_class !== 'production'
    && envelope.command.movement_class !== 'uom_rebase'
    && envelope.command.movement_class !== 'reverse'
    && envelope.command.movement_class !== 'lot_state'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['master_effects'],
      message: 'Gli effetti master sono ammessi solo per production, reverse, lot_state e uom_rebase',
    });
  }
  if (
    envelope.command.movement_class === 'production'
    && envelope.master_effects.length > 0
  ) {
    const productionCommand = envelope.command;
    const onlyKeys = (
      patch: Record<string, unknown>,
      allowed: readonly string[],
    ): boolean => Object.keys(patch).every((key) => allowed.includes(key));
    const isRecord = (value: unknown): value is Record<string, unknown> => (
      value != null && typeof value === 'object' && !Array.isArray(value)
    );
    const isUuid = (value: unknown): value is string => (
      typeof value === 'string' && z.string().uuid().safeParse(value).success
    );
    const effects = envelope.master_effects;
    const foodInserts = effects.filter((effect) => (
      effect.table === 'foods' && effect.op === 'insert'
    ));
    const foodUpdates = effects.filter((effect) => (
      effect.table === 'foods' && effect.op === 'update'
    ));
    const productionInserts = effects.filter((effect) => (
      effect.table === 'productions' && effect.op === 'insert'
    ));
    const productionUpdates = effects.filter((effect) => (
      effect.table === 'productions' && effect.op === 'update'
    ));
    const productionArchives = effects.filter((effect) => (
      effect.table === 'productions' && effect.op === 'archive'
    ));
    const intents = effects.filter((effect) => (
      effect.table === 'production_label_print_intents_v1'
      && effect.op === 'insert'
    ));
    const categorizedCount = (
      foodInserts.length
      + foodUpdates.length
      + productionInserts.length
      + productionUpdates.length
      + productionArchives.length
      + intents.length
    );
    const productionInsert = productionInserts[0];
    const productionInsertInputs = productionInsert?.patch.inputs;

    const validCreateShape = (
      productionInserts.length === 1
      && productionUpdates.length === 0
      && productionArchives.length === 0
      && foodInserts.length + foodUpdates.length <= 1
      && intents.length <= 1
      && categorizedCount === effects.length
    );
    const validTraceShape = (
      productionUpdates.length > 0
      && productionUpdates.length === effects.length
      && productionCommand.production_id == null
    );
    const validVoidShape = (
      productionArchives.length === 1
      && intents.length === 1
      && effects.length === 2
      && categorizedCount === effects.length
    );
    const validIntentShape = (
      intents.length === 1
      && effects.length === 1
    );

    let semanticallyValid = (
      foodInserts.length <= 1
      && foodUpdates.length <= 1
      && productionInserts.length <= 1
      && productionArchives.length <= 1
      && intents.length <= 1
      && (
        validCreateShape
        || validTraceShape
        || validVoidShape
        || validIntentShape
      )
    );

    for (const effect of effects) {
      const patch = effect.patch;
      if (effect.table === 'productions' && effect.op === 'insert') {
        semanticallyValid = semanticallyValid && (
          productionCommand.production_id != null
          && effect.id === productionCommand.production_id
          && patch.id === effect.id
          && patch.tenant_id === productionCommand.tenant_id
          && patch.business_id === productionCommand.business_id
          && isUuid(patch.created_by)
          && (
            !Object.prototype.hasOwnProperty.call(patch, 'inputs')
            || (
              Array.isArray(patch.inputs)
              && patch.inputs.length <= 2_000
            )
          )
        );
      } else if (
        effect.table === 'productions'
        && effect.op === 'update'
      ) {
        semanticallyValid = semanticallyValid && (
          patch.id === effect.id
          && patch.tenant_id === productionCommand.tenant_id
          && Array.isArray(patch.inputs)
          && patch.inputs.length <= 2_000
          && Array.isArray(patch.allergens_aggregated)
          && onlyKeys(patch, [
            'id',
            'tenant_id',
            'inputs',
            'allergens_aggregated',
          ])
        );
      } else if (
        effect.table === 'productions'
        && effect.op === 'archive'
      ) {
        semanticallyValid = semanticallyValid && (
          productionCommand.production_id != null
          && effect.id === productionCommand.production_id
          && patch.id === effect.id
          && patch.tenant_id === productionCommand.tenant_id
          && typeof patch.archived_at === 'string'
          && onlyKeys(patch, ['id', 'tenant_id', 'archived_at'])
        );
      } else if (effect.table === 'foods' && effect.op === 'insert') {
        const outputBound = productionCommand.outputs.some(
          (output) => output.food_id === effect.id,
        );
        const recipeBound = (
          productionInsert != null
          && typeof patch.recipe_id === 'string'
          && productionInsert.patch.recipe_id === patch.recipe_id
        );
        const derivedShelfLifeValid = (
          (typeof patch.shelf_life_days_opened === 'number'
            && Number.isFinite(patch.shelf_life_days_opened)
            && patch.shelf_life_days_opened > 0
            && patch.name === productionInsert?.patch.output_name
            && / \(aperto\)$/.test(String(patch.name)))
          || (typeof patch.shelf_life_hours_thawed === 'number'
            && Number.isFinite(patch.shelf_life_hours_thawed)
            && patch.shelf_life_hours_thawed > 0
            && patch.name === productionInsert?.patch.output_name
            && / \(scongelato\)$/.test(String(patch.name)))
        );
        const derivedBound = (
          outputBound
          && patch.creation_source === 'quick_label'
          && patch.recipe_id == null
          && productionCommand.inputs.length === 1
          && productionCommand.inputs[0]?.food_id !== effect.id
          && intents.length === 1
          && (
            (/ \(aperto\)$/.test(String(patch.name))
              && intents[0]?.patch.label_payload != null
              && isRecord(intents[0].patch.label_payload)
              && intents[0].patch.label_payload.preset === 'ingrediente_aperto')
            || (/ \(scongelato\)$/.test(String(patch.name))
              && intents[0]?.patch.label_payload != null
              && isRecord(intents[0].patch.label_payload)
              && intents[0].patch.label_payload.preset === 'decongelato')
          )
          && derivedShelfLifeValid
        );
        semanticallyValid = semanticallyValid && (
          patch.id === effect.id
          && patch.tenant_id === productionCommand.tenant_id
          && (isUuid(patch.recipe_id) || derivedBound)
          && onlyKeys(patch, [
            'id',
            'tenant_id',
            'name',
            'description',
            'category',
            'default_uom',
            'storage_type',
            'allergens',
            'recipe_id',
            'creation_source',
            'shelf_life_hours_thawed',
            'shelf_life_days_opened',
          ])
          && (outputBound || recipeBound)
        );
      } else if (effect.table === 'foods' && effect.op === 'update') {
        const shelfLifeKeys = [
          'shelf_life_hours_thawed',
          'shelf_life_days_opened',
        ] as const;
        const presentShelfLifeKeys = shelfLifeKeys.filter(
          (key) => Object.prototype.hasOwnProperty.call(patch, key),
        );
        const shelfLifeValue = (
          presentShelfLifeKeys.length === 1
            ? patch[presentShelfLifeKeys[0]]
            : null
        );
        const commandInputBound = productionCommand.inputs.some(
          (input) => input.food_id === effect.id,
        );
        const commandOutputBound = productionCommand.outputs.some(
          (output) => output.food_id === effect.id,
        );
        const productionInputBound = (
          Array.isArray(productionInsertInputs)
          && productionInsertInputs.some((input) => (
            isRecord(input) && input.food_id === effect.id
          ))
        );
        semanticallyValid = semanticallyValid && (
          patch.id === effect.id
          && (
            !Object.prototype.hasOwnProperty.call(patch, 'tenant_id')
            || patch.tenant_id === productionCommand.tenant_id
          )
          && presentShelfLifeKeys.length === 1
          && typeof shelfLifeValue === 'number'
          && Number.isFinite(shelfLifeValue)
          && shelfLifeValue > 0
          && onlyKeys(patch, [
            'id',
            'tenant_id',
            'shelf_life_hours_thawed',
            'shelf_life_days_opened',
          ])
          && (commandInputBound || commandOutputBound || productionInputBound)
        );
      } else if (
        effect.table === 'production_label_print_intents_v1'
        && effect.op === 'insert'
      ) {
        semanticallyValid = semanticallyValid && (
          productionCommand.production_id != null
          && patch.id === effect.id
          && patch.tenant_id === productionCommand.tenant_id
          && patch.business_id === productionCommand.business_id
          && patch.production_id === productionCommand.production_id
          && patch.command_id === productionCommand.command_id
          && (
            patch.audit_kind === 'label_printed'
            || patch.audit_kind === 'label_voided'
          )
          && isRecord(patch.label_payload)
          && onlyKeys(patch, [
            'id',
            'tenant_id',
            'business_id',
            'production_id',
            'command_id',
            'audit_client_uuid',
            'audit_kind',
            'label_payload',
            'state',
            'print_job',
            'requested_at',
          ])
        );
      } else {
        semanticallyValid = false;
      }
    }

    if (
      productionInserts.length === 1
      && intents.length === 1
      && intents[0]?.patch.audit_kind !== 'label_printed'
    ) {
      semanticallyValid = false;
    }
    if (
      productionArchives.length === 1
      && (
        intents[0]?.patch.audit_kind !== 'label_voided'
        || intents[0]?.patch.production_id !== productionArchives[0]?.id
      )
    ) {
      semanticallyValid = false;
    }

    if (!semanticallyValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['master_effects'],
        message: 'Gli effetti master production non sono coerenti con lavorazione, trace, prodotto finito o etichetta',
      });
    }
  }
  if (
    envelope.command.movement_class === 'reverse'
    && envelope.master_effects.length > 0
  ) {
    const isEmptyArray = (value: unknown): boolean => (
      Array.isArray(value) && value.length === 0
    );
    const onlyKeys = (
      patch: Record<string, unknown>,
      allowed: readonly string[],
    ): boolean => Object.keys(patch).every((key) => allowed.includes(key));
    const traceResets = envelope.master_effects.filter((effect) => (
      effect.table === 'productions'
      && effect.op === 'update'
      && effect.patch.id === effect.id
      && effect.patch.tenant_id === envelope.command.tenant_id
      && isEmptyArray(effect.patch.inputs)
      && isEmptyArray(effect.patch.allergens_aggregated)
      && onlyKeys(effect.patch, [
        'id',
        'tenant_id',
        'inputs',
        'allergens_aggregated',
      ])
    ));
    const archives = envelope.master_effects.filter((effect) => (
      effect.table === 'productions'
      && effect.op === 'archive'
      && effect.patch.id === effect.id
      && effect.patch.tenant_id === envelope.command.tenant_id
      && typeof effect.patch.archived_at === 'string'
      && onlyKeys(effect.patch, ['id', 'tenant_id', 'archived_at'])
    ));
    const voidIntents = envelope.master_effects.filter((effect) => (
      effect.table === 'production_label_print_intents_v1'
      && effect.op === 'insert'
      && effect.patch.id === effect.id
      && effect.patch.tenant_id === envelope.command.tenant_id
      && effect.patch.command_id === envelope.command.command_id
      && effect.patch.audit_kind === 'label_voided'
      && typeof effect.patch.production_id === 'string'
      && typeof effect.patch.business_id === 'string'
      && onlyKeys(effect.patch, [
        'id',
        'tenant_id',
        'business_id',
        'production_id',
        'command_id',
        'audit_client_uuid',
        'audit_kind',
        'label_payload',
        'state',
        'print_job',
        'requested_at',
      ])
    ));
    const validTraceReset = (
      traceResets.length === envelope.master_effects.length
    );
    const validVoid = (
      traceResets.length === 0
      && archives.length <= 1
      && voidIntents.length <= 1
      && archives.length + voidIntents.length
           === envelope.master_effects.length
      && (
        archives.length === 0
        || voidIntents.length === 0
        || archives[0]?.id === voidIntents[0]?.patch.production_id
      )
    );
    if (!validTraceReset && !validVoid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['master_effects'],
        message: 'Un reverse può azzerare la trace o annullare la produzione/etichetta collegata',
      });
    }
  }
  if (
    envelope.command.movement_class === 'lot_state'
    && envelope.master_effects.length > 0
  ) {
    const lotStateCommand = envelope.command;
    const line = lotStateCommand.lines[0];
    const onlyKeys = (
      patch: Record<string, unknown>,
      allowed: readonly string[],
    ): boolean => Object.keys(patch).every((key) => allowed.includes(key));
    const sourceProductionId = line?.source_production_id;
    const isUuid = (value: unknown): value is string => (
      typeof value === 'string' && z.string().uuid().safeParse(value).success
    );
    const productionInserts = envelope.master_effects.filter((effect) => (
      effect.table === 'productions' && effect.op === 'insert'
    ));
    const foodInserts = envelope.master_effects.filter((effect) => (
      effect.table === 'foods' && effect.op === 'insert'
    ));
    const archives = envelope.master_effects.filter((effect) => (
      effect.table === 'productions'
      && effect.op === 'archive'
      && effect.id === sourceProductionId
      && effect.patch.id === sourceProductionId
      && effect.patch.tenant_id === lotStateCommand.tenant_id
      && typeof effect.patch.archived_at === 'string'
      && onlyKeys(effect.patch, ['id', 'tenant_id', 'archived_at'])
    ));
    const voidIntents = envelope.master_effects.filter((effect) => (
      effect.table === 'production_label_print_intents_v1'
      && effect.op === 'insert'
      && effect.patch.id === effect.id
      && effect.patch.tenant_id === lotStateCommand.tenant_id
      && effect.patch.business_id === lotStateCommand.business_id
      && effect.patch.production_id === sourceProductionId
      && effect.patch.command_id === lotStateCommand.command_id
      && effect.patch.audit_kind === 'label_voided'
      && onlyKeys(effect.patch, [
        'id',
        'tenant_id',
        'business_id',
        'production_id',
        'command_id',
        'audit_client_uuid',
        'audit_kind',
        'label_payload',
        'state',
        'print_job',
        'requested_at',
      ])
    ));
    const printIntents = envelope.master_effects.filter((effect) => (
      effect.table === 'production_label_print_intents_v1'
      && effect.op === 'insert'
      && effect.patch.id === effect.id
      && effect.patch.tenant_id === lotStateCommand.tenant_id
      && effect.patch.business_id === lotStateCommand.business_id
      && effect.patch.production_id === sourceProductionId
      && effect.patch.command_id === lotStateCommand.command_id
      && effect.patch.audit_kind === 'label_printed'
      && typeof effect.patch.label_payload === 'object'
      && effect.patch.label_payload != null
      && !Array.isArray(effect.patch.label_payload)
      && onlyKeys(effect.patch, [
        'id',
        'tenant_id',
        'business_id',
        'production_id',
        'command_id',
        'audit_client_uuid',
        'audit_kind',
        'label_payload',
        'state',
        'print_job',
        'requested_at',
      ])
    ));
    const productionInsert = productionInserts[0];
    const validRegister = (
      lotStateCommand.lines.length === 1
      && line?.action === 'register'
      && isUuid(sourceProductionId)
      && productionInserts.length === 1
      && foodInserts.length <= 1
      && printIntents.length === 1
      && productionInserts.length + foodInserts.length + printIntents.length
           === envelope.master_effects.length
      && productionInsert?.id === sourceProductionId
      && productionInsert.patch.id === sourceProductionId
      && productionInsert.patch.tenant_id === lotStateCommand.tenant_id
      && productionInsert.patch.business_id === lotStateCommand.business_id
      && isUuid(productionInsert.patch.created_by)
      && Array.isArray(productionInsert.patch.inputs)
      && productionInsert.patch.inputs.length <= 2_000
      && foodInserts.every((effect) => (
        effect.id === line.food_id
        && effect.patch.id === effect.id
        && effect.patch.tenant_id === lotStateCommand.tenant_id
        && isUuid(effect.patch.recipe_id)
        && effect.patch.recipe_id === productionInsert.patch.recipe_id
        && (!Object.prototype.hasOwnProperty.call(effect.patch, 'creation_source')
          || effect.patch.creation_source === 'quick_label')
        && onlyKeys(effect.patch, [
          'id',
          'tenant_id',
          'name',
          'description',
          'category',
          'default_uom',
          'storage_type',
          'allergens',
          'recipe_id',
          'creation_source',
        ])
      ))
    );
    const validVoid = (
      lotStateCommand.lines.length === 1
      && line?.action === 'close'
      && isUuid(sourceProductionId)
      && archives.length <= 1
      && voidIntents.length === 1
      && archives.length + voidIntents.length
           === envelope.master_effects.length
    );
    if (!validRegister && !validVoid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['master_effects'],
        message: 'lot_state ammette solo produzione ed etichetta collegate alla registrazione o al suo annullamento',
      });
    }
  }
  if (envelope.command.movement_class === 'uom_rebase') {
    const firstLine = envelope.command.lines[0];
    const effect = envelope.master_effects[0];
    if (
      envelope.master_effects.length !== 1
      || !firstLine
      || effect?.table !== 'foods'
      || effect.op !== 'update'
      || effect.id !== firstLine.food_id
      || effect.patch.default_uom !== firstLine.stock_uom
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['master_effects'],
        message: 'uom_rebase richiede un solo update foods coerente con la nuova UdM',
      });
    }
  }
});
export type StockCommandEnvelopeV1 = z.infer<typeof StockCommandEnvelopeV1Schema>;

export const ShipmentStockVoidPreparationV1Schema = z.object({
  schema: z.literal('trackeat.shipment-stock-void-preparation'),
  version: z.literal(1),
  shipment_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  movement_class: z.enum(['dispatch', 'transfer']),
  original_command_id: z.string().uuid(),
  original_command_digest: z.string().regex(/^[0-9a-f]{64}$/),
  movements: z.array(
    ReversibleStockMovementV1Schema.and(z.object({
      max_reversible_qty: z.number().finite().nonnegative(),
    })),
  ).min(1).max(2_000),
});
export type ShipmentStockVoidPreparationV1 = z.infer<
  typeof ShipmentStockVoidPreparationV1Schema
>;

export const ShipmentStockVoidResponseV1Schema = z.object({
  status: z.literal('void'),
  doc_number: z.number().int().positive(),
  doc_year: z.number().int().min(2_000),
  stock_result: z.record(z.unknown()),
});
export type ShipmentStockVoidResponseV1 = z.infer<
  typeof ShipmentStockVoidResponseV1Schema
>;

export const ShipmentWithoutStockIssueResponseV1Schema = z.object({
  doc_number: z.number().int().positive(),
  doc_year: z.number().int().min(2_000),
  shipment: z.record(z.unknown()),
  audit_event: z.record(z.unknown()),
  replayed: z.boolean(),
  writeback: z.record(z.unknown()),
});
export type ShipmentWithoutStockIssueResponseV1 = z.infer<
  typeof ShipmentWithoutStockIssueResponseV1Schema
>;

export const ShipmentWithoutStockVoidResponseV1Schema = z.object({
  status: z.literal('void'),
  shipment_id: z.string().uuid(),
  doc_number: z.number().int().positive().nullable(),
  doc_year: z.number().int().min(2_000).nullable(),
  version: z.number().int().nonnegative(),
  replayed: z.boolean(),
  shipment: z.record(z.unknown()),
  audit_event: z.record(z.unknown()),
  writeback: z.record(z.unknown()),
});
export type ShipmentWithoutStockVoidResponseV1 = z.infer<
  typeof ShipmentWithoutStockVoidResponseV1Schema
>;

/**
 * Stable id for the receipt-bound stock reversal of a shipment.
 *
 * The server stores this id on `shipments.stock_void_command_id`: deriving it
 * from the shipment makes a retry safe even when the first RPC committed but
 * its response never reached the client.
 */
export async function shipmentStockVoidCommandIdFor(
  tenantId: string,
  shipmentId: string,
): Promise<string> {
  const data = new TextEncoder().encode(
    `shipment-stock-void-v1|${tenantId}|${shipmentId}`,
  );
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
  const bytes = digest.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(
    bytes,
    (value) => value.toString(16).padStart(2, '0'),
  ).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function buildShipmentStockVoidCommand(
  preparationInput: ShipmentStockVoidPreparationV1,
  commandId: string,
  reason: string,
): StockCommandV1 {
  const preparation = ShipmentStockVoidPreparationV1Schema.parse(preparationInput);
  if (
    preparation.movement_class === 'transfer'
    && preparation.movements.length % 2 !== 0
  ) {
    throw new Error(
      'Annullamento trasferimento non disponibile: coppia di movimenti incompleta',
    );
  }

  const movements = preparation.movements.map((prepared, index) => {
    const {
      max_reversible_qty: rawMax,
      ...movement
    } = prepared;
    const tolerance = Math.max(1, Math.abs(movement.stock_qty)) * 1e-12;
    if (preparation.movement_class === 'dispatch') {
      if (Math.abs(rawMax - movement.stock_qty) > tolerance) {
        throw new Error(
          'Annullamento completo non disponibile: parte della merce spedita risulta già consumata',
        );
      }
      return movement;
    }

    if (rawMax <= tolerance || rawMax > movement.stock_qty + tolerance) {
      throw new Error(
        'Annullamento trasferimento non disponibile: nessuna quantità simmetrica reversibile',
      );
    }

    const paired = preparation.movements[index % 2 === 0 ? index + 1 : index - 1];
    const pairTolerance = Math.max(
      1,
      Math.abs(rawMax),
      Math.abs(paired?.max_reversible_qty ?? 0),
    ) * 1e-12;
    if (
      paired == null
      || Math.abs(rawMax - paired.max_reversible_qty) > pairTolerance
    ) {
      throw new Error(
        'Annullamento trasferimento non disponibile: quantità reversibili non simmetriche',
      );
    }

    const ratio = rawMax / movement.stock_qty;
    return {
      ...movement,
      source_qty: movement.source_qty * ratio,
      source_line_total: movement.source_line_total == null
        ? null
        : movement.source_line_total * ratio,
      stock_qty: rawMax,
    };
  });

  return StockCommandV1Schema.parse({
    version: 1,
    command_id: commandId,
    tenant_id: preparation.tenant_id,
    movement_class: 'reverse',
    economic_effect: 'reversal',
    reason,
    reverses_command_id: preparation.original_command_id,
    movements,
  });
}

export const SyncPullRequestSchema = z.object({
  since_rev: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(5000).default(500),
  // T-128: forza re-pull integrale ignorando since_rev (auto-recovery).
  force_full: z.boolean().optional(),
  // T-182: cursore PER-TENANT (server_rev è per-tenant). Mappa {tenant_id: rev}.
  // Quando presente il server lo usa al posto di since_rev e scopa gli eventi al
  // tenant attivo: risolve il salto degli eventi dei tenant a rev basso per i
  // multi-tenant (consulente/owner). Vecchi server lo ignorano.
  since_rev_by_tenant: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export type SyncPullRequest = z.infer<typeof SyncPullRequestSchema>;

export const SyncPullResponseSchema = z.object({
  events: z.array(z.record(z.unknown())),
  masters_changed: z.array(
    z.object({
      table: z.string(),
      id: z.string().uuid(),
      row: z.record(z.unknown()),
      version: z.number().int().nonnegative(),
    }),
  ),
  max_rev: z.number().int().nonnegative(),
  has_more: z.boolean(),
  // T-128: campi diagnostici opzionali (vecchi server li omettono, vecchi
  // client li ignorano). Il server li popola quando rileva o applica un
  // auto-reset del cursore.
  stale_cursor: z.boolean().optional(),
  auto_reset: z.boolean().optional(),
  user_head_rev: z.number().int().nonnegative().optional(),
  // T-182: max server_rev per-tenant restituito dal ramo per-tenant. Il client
  // aggiornato avanza il cursore di ogni tenant da qui. Vecchi server lo omettono.
  max_rev_by_tenant: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export type SyncPullResponse = z.infer<typeof SyncPullResponseSchema>;

export const SyncPushRequestSchema = z.object({
  // Additivi: un server nuovo continua ad accettare request storiche che li
  // omettono; un server storico ignora questi campi JSON sconosciuti.
  sync_protocol: z.literal(1).optional(),
  client_version: z.string().min(1).max(200).optional(),
  client_capabilities: z
    .array(z.enum([
      'stock_command_v1',
      'stock_command_dead_letter_v1',
      // T-700: il client sa isolare una singola operazione avvelenata senza
      // fermare le altre code. Campo additivo: i server precedenti lo ignorano
      // e i client precedenti continuano a inviare le capability già note.
      'sync_poison_isolation_v1',
    ]))
    .default([]),
  events: z.array(SyncEventInputSchema).default([]),
  master_ops: z.array(MasterOpSchema).default([]),
  stock_commands_v1: z.array(StockCommandEnvelopeV1Schema).max(50).default([]),
});
export type SyncPushRequest = z.infer<typeof SyncPushRequestSchema>;

export const SyncPushResponseSchema = z.object({
  sync_protocol: z.literal(1).optional(),
  server_capabilities: z
    // Le capability sono per definizione estensibili: un client non deve
    // fallire il parse quando un server futuro ne aggiunge una nuova.
    .array(z.string().min(1).max(100))
    .optional(),
  accepted_events: z.array(z.string().uuid()),
  rejected_events: z.array(
    z.object({
      client_uuid: z.string().uuid(),
      reason: z.string(),
    }),
  ),
  accepted_masters: z.array(z.string().uuid()),
  rejected_masters: z.array(
    z.object({
      id: z.string().uuid(),
      reason: z.string(),
    }),
  ),
  conflicts: z.array(
    z.object({
      table: z.string(),
      id: z.string().uuid(),
      server_value: z.record(z.unknown()),
      client_patch: z.record(z.unknown()),
      resolution: z.enum(['lww_server', 'lww_client', 'manual']),
    }),
  ),
  assigned_revs: z.record(z.number()),
  accepted_stock_commands: z.array(
    z.object({
      command_id: z.string().uuid(),
      command_digest: z.string().regex(/^[0-9a-f]{64}$/),
      result: z.record(z.unknown()),
    }),
  ).default([]),
  rejected_stock_commands: z.array(
    z.object({
      command_id: z.string().uuid(),
      command_digest: z.string().regex(/^[0-9a-f]{64}$/),
      reason: z.string(),
      retryable: z.boolean(),
      details: z.unknown().optional(),
    }),
  ).default([]),
});
export type SyncPushResponse = z.infer<typeof SyncPushResponseSchema>;

export const SYNC_BATCH_SIZE = 500;
// T-312/#5: cadenza del poll di background. Alzata da 30s a 90s (decisione owner
// 2026-06-29): con molti dispositivi attivi un poll ogni 30s moltiplica le
// chiamate sync-pull/head verso il server senza beneficio (le scritture restano
// immediate via push ottimistico; il pull serve solo a vedere le modifiche
// ALTRUI, per cui 90s è ampiamente sufficiente). Offline-first invariato: ogni
// azione locale resta istantanea, questa è solo la frequenza di riconciliazione.
export const SYNC_INTERVAL_MS = 90_000;
// Max ops sent to sync-push in a SINGLE request. The server processes ops
// sequentially (~60ms each), so a large batch (e.g. hundreds of ops queued at
// once after a local-data reset) can exceed the client fetch timeout, abort,
// and never apply the response — wedging the outbox. Pushing in small chunks
// keeps every request comfortably under SYNC_FETCH_TIMEOUT_MS; a backlog drains
// across several chunks instead of one doomed mega-request.
export const SYNC_PUSH_BATCH_SIZE = 100;
